/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useRef, useEffect } from "react";
import "./Navbar.css";
import { FaSignOutAlt } from "react-icons/fa";
import { useAuth } from "../../../context/auth/AuthContext";

import {
  FaBars,
  FaSun,
  FaMoon,
  FaBullhorn,
  FaTimes,
  FaSearch,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useColorMode, IconButton } from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { goToLoginScreen } from "../../../navigation/limitedBackHistory";
import logo from "../../../assets/icon.png";
import { useSearch } from "../../context/SearchContext";

export default function Navbar({ onSidebarToggle }) {
  const { i18n, t } = useTranslation();
  const isArabic = i18n.language === "ar";
  const { colorMode, toggleColorMode } = useColorMode();
  const { query, setQuery, handleSearch } = useSearch();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  const switchLanguage = () => {
    const newLang = isArabic ? "en" : "ar";
    i18n.changeLanguage(newLang);
    localStorage.setItem("lang", newLang);
    document.documentElement.lang = newLang;
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
  };
  const { logout } = useAuth();
  const handleLogout = () => {
    logout();                    // مسح التوكن و البيانات من الكونتكست
    localStorage.clear();        // تنظيف كامل للجلسة
    goToLoginScreen(navigate);   // شاشة تسجيل الدخول الجديدة
  };
  
  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setQuery("");
  };

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  return (
    <nav
      className={`navbar-container ${isArabic ? "rtl" : "ltr"} ${
        colorMode === "dark" ? "dark" : "light"
      }`}
    >
      <div className="navbar-left">
        <FaBars
          className="menu-icon mobile-only"
          onClick={onSidebarToggle}
          title={t("admin.menu")}
        />
        <img src={logo} alt="Logo" className="logo" />
      </div>

      {/* Desktop Search */}
      <div className="navbar-search desktop-search">
        <input
          type="text"
          placeholder={t("admin.search")}
          className="search-input"
          value={query}
          onChange={(e) => {
            const value = e.target.value;
            setQuery(value);
            if (value.trim()) {
              handleSearch(value);
            }
          }}
          onKeyPress={(e) => {
            if (e.key === "Enter" && query.trim()) {
              navigate("/admin/search");
            }
          }}
        />
        {query && (
          <FaTimes
            className="clear-search"
            onClick={() => {
              setQuery("");
              navigate(-1);
            }}
            title={isArabic ? "مسح البحث" : "Clear Search"}
          />
        )}
      </div>

      {/* Mobile Search Icon */}
      <div className="mobile-search-icon" onClick={toggleSearch}>
        <FaSearch />
      </div>

      {/* Mobile Search Input (Expandable) - Full width on new line */}
      {isSearchOpen && (
        <div className="navbar-search mobile-search-expanded">
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t("admin.search")}
            className="search-input"
            value={query}
            onChange={(e) => {
              const value = e.target.value;
              setQuery(value);
              if (value.trim()) {
                handleSearch(value);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter" && query.trim()) {
                navigate("/admin/search");
                setIsSearchOpen(false);
              }
            }}
          />
          <FaTimes
            className="close-search-mobile"
            onClick={() => {
              closeSearch();
              navigate(-1);
            }}
            title={isArabic ? "إغلاق" : "Close"}
          />
        </div>
      )}
      
      <div className="navbar-right">
        <button onClick={switchLanguage} className="lang-btn">
          {isArabic ? "EN" : "AR"}
        </button>
<IconButton
  aria-label={isArabic ? "تسجيل الخروج" : "Logout"}
  icon={<FaSignOutAlt />}
  onClick={handleLogout}
  variant="ghost"
  color={colorMode === "light" ? "#6A11CB" : "#FFD700"}
  fontSize="18px"
  _hover={{ bg: "transparent", transform: "scale(1.1)" }}
  title={isArabic ? "تسجيل الخروج" : "Logout"}
/>

        <IconButton
          as={Link}
          to="/admin/notifications"
          aria-label={t("admin.notifications")}
          icon={<FaBullhorn />}
          variant="ghost"
          color={colorMode === "light" ? "#6A11CB" : "#FFD700"}
          fontSize="18px"
          _hover={{ bg: "transparent", transform: "scale(1.1)" }}
          title={t("admin.notifications")}
        />

        <IconButton
          aria-label="Toggle dark mode"
          icon={colorMode === "light" ? <FaMoon /> : <FaSun />}
          onClick={toggleColorMode}
          variant="ghost"
          color={colorMode === "light" ? "#6A11CB" : "#FFD700"}
          fontSize="18px"
          _hover={{ bg: "transparent", transform: "scale(1.1)" }}
        />
      </div>
    </nav>
  );
}
